import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import ToolsSelect from "../kit/ToolsSelect";
import ToolsModal from "../kit/ToolsModal";
import WComponent from "../../../common/WComponent";
import FileDropZone from "../../../common/file_drop_zone/FileDropZone";
import UtilityService from "../../../../services/UtilityService";
import BookkeepingService from "../../../../services/BookkeepingService";
import CURRENCIES from "../../../../constants/Currencies";
import "./bank-account-review.css";

const CURRENCY_OPTIONS = CURRENCIES.map(currency => ({ value: currency.code, label: `${currency.code} — ${currency.name}` }));

function accountRowFromTheFile({ account }) {
	return {
		name: account.name,
		included: true,
		isOnTheFile: true,
		bankAccountId: account.id,
		postsTo: account.postsTo,
		postsToAccountId: account.accountId || null,
		storedCurrency: account.currency,
	};
}

function accountRowFromAName({ name, postsToAccountId = null }) {
	return { name, included: true, isOnTheFile: false, bankAccountId: null, postsTo: null, postsToAccountId, storedCurrency: null, identityConfirmedAsNew: false };
}

class BankAccountReview extends WComponent {
	constructor(props) {
		super(props);

		const rowsFromTheFile = (props.accountsOnTheFile || []).map(account => accountRowFromTheFile({ account }));
		const namesFromTheFile = new Set(rowsFromTheFile.map(row => row.name));
		const namesStillToList = (props.detectedNames || []).filter(name => !namesFromTheFile.has(name));
		const chosenChartAccounts = props.initialPostsToAccountIdByBankAccount || {};
		const rowsFromNames = namesStillToList.map(name => accountRowFromAName({ name, postsToAccountId: chosenChartAccounts[name] || null }));

		this.state = {
			accounts: [...rowsFromTheFile, ...rowsFromNames],
			fileByAccount: props.initialFileByAccount ? { ...props.initialFileByAccount } : {},
			currencyByAccount: props.initialCurrencyByAccount ? { ...props.initialCurrencyByAccount } : {},
			customName: "",
			currencyChangeToConfirm: null,
			changingCurrency: false,
		};
	}

	getRemainingAccountOptions = () => {
		const allAccountNames = this.props.allAccountNames || [];
		const usedNames = this.state.accounts.map(account => account.name);
		const remainingNames = allAccountNames.filter(name => !usedNames.includes(name));
		const options = remainingNames.map(name => ({ value: name, label: name }));
		return options;
	};

	currencyCodeFor = account => {
		const chosen = this.state.currencyByAccount[account.name];
		if (chosen) return chosen;
		if (account.storedCurrency) return account.storedCurrency;
		const guessed = BookkeepingService.guessAccountCurrency({ accountName: account.name });
		return guessed;
	};

	onSelectCurrency = (accountName, currencyCode) => {
		this.setState(prev => ({ currencyByAccount: { ...prev.currencyByAccount, [accountName]: currencyCode } }));
	};

	onPickCurrency = ({ account, currencyCode }) => {
		const theAccountAlreadyHasOne = account.isOnTheFile && !!account.storedCurrency;
		const isADifferentOne = currencyCode !== account.storedCurrency;
		if (theAccountAlreadyHasOne && isADifferentOne) {
			this.update({ currencyChangeToConfirm: { account, currencyCode } });
			return;
		}
		this.onSelectCurrency(account.name, currencyCode);
	};

	onCancelCurrencyChange = () => this.update({ currencyChangeToConfirm: null });

	onConfirmCurrencyChange = async () => {
		const { onChangeCurrency } = this.props;
		const { currencyChangeToConfirm } = this.state;
		if (!currencyChangeToConfirm || !onChangeCurrency) return;

		const { account, currencyCode } = currencyChangeToConfirm;
		await this.update({ changingCurrency: true });
		const outcome = await onChangeCurrency({ bankAccountId: account.bankAccountId, accountName: account.name, currency: currencyCode });
		await this.update({ changingCurrency: false, currencyChangeToConfirm: null });
		if (!outcome || !outcome.changed) return;

		this.setState(prev => {
			const accounts = prev.accounts.map(row => (row.name === account.name ? { ...row, storedCurrency: currencyCode } : row));
			const currencyByAccount = { ...prev.currencyByAccount };
			delete currencyByAccount[account.name];
			return { accounts, currencyByAccount };
		});
	};

	onToggleIncluded = index => {
		this.setState(prev => {
			const accounts = prev.accounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, included: !account.included } : account
			);
			return { accounts };
		});
	};

	onRenameAccount = (index, newName) => {
		this.setState(prev => {
			const previousName = prev.accounts[index].name;
			const accounts = prev.accounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, name: newName } : account
			);
			const fileByAccount = { ...prev.fileByAccount };
			const currencyByAccount = { ...prev.currencyByAccount };
			if (fileByAccount[previousName]) {
				fileByAccount[newName] = fileByAccount[previousName];
				delete fileByAccount[previousName];
			}
			if (currencyByAccount[previousName]) {
				currencyByAccount[newName] = currencyByAccount[previousName];
				delete currencyByAccount[previousName];
			}
			return { accounts, fileByAccount, currencyByAccount };
		});
	};

	onRemoveAccount = index => {
		this.setState(prev => {
			const removedName = prev.accounts[index].name;
			const accounts = prev.accounts.filter((account, accountIndex) => accountIndex !== index);
			const fileByAccount = { ...prev.fileByAccount };
			const currencyByAccount = { ...prev.currencyByAccount };
			delete fileByAccount[removedName];
			delete currencyByAccount[removedName];
			return { accounts, fileByAccount, currencyByAccount };
		});
	};

	onConfirmRowIsNewAccount = index => {
		this.setState(prev => ({
			accounts: prev.accounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, identityConfirmedAsNew: true } : account
			),
		}));
	};

	onTieRowToExistingAccount = ({ index, existingAccountName }) => {
		this.setState(prev => {
			const row = prev.accounts[index];
			const existingUpload = prev.fileByAccount[existingAccountName];
			const rowUpload = prev.fileByAccount[row.name];

			if (rowUpload && existingUpload && existingUpload.kind !== rowUpload.kind) {
				UtilityService.alert({ type: "error", text: `${existingAccountName} already has a ${existingUpload.kind === "pdf" ? "PDF" : "spreadsheet"} statement attached — this file can't be merged into it.` });
				return null;
			}

			const accounts = prev.accounts.filter((account, accountIndex) => accountIndex !== index);
			const fileByAccount = { ...prev.fileByAccount };
			const currencyByAccount = { ...prev.currencyByAccount };

			if (rowUpload) {
				const newFiles = existingUpload ? UtilityService.getFilesNotAlreadyAdded({ existingFiles: existingUpload.files, candidateFiles: rowUpload.files }) : rowUpload.files;
				fileByAccount[existingAccountName] = existingUpload
					? { kind: existingUpload.kind, files: [...existingUpload.files, ...newFiles] }
					: rowUpload;
			}
			delete fileByAccount[row.name];

			if (!(existingAccountName in currencyByAccount) && row.name in currencyByAccount) {
				currencyByAccount[existingAccountName] = currencyByAccount[row.name];
			}
			delete currencyByAccount[row.name];

			return { accounts, fileByAccount, currencyByAccount };
		});
	};

	onSelectFileForAccount = (accountName, event) => {
		const selectedFiles = Array.from(event.target.files || []);
		event.target.value = "";
		if (selectedFiles.length === 0) return;

		const pdfFiles = selectedFiles.filter(file => UtilityService.isPdfFile(file.name));
		const allArePdfs = pdfFiles.length === selectedFiles.length;

		if (allArePdfs) {
			this.setState(prev => ({ fileByAccount: { ...prev.fileByAccount, [accountName]: { files: pdfFiles, kind: "pdf" } } }));
			return;
		}

		const spreadsheetFiles = selectedFiles.filter(file => !UtilityService.isPdfFile(file.name));
		this.setState(prev => ({ fileByAccount: { ...prev.fileByAccount, [accountName]: { files: spreadsheetFiles, kind: "spreadsheet" } } }));
	};

	onRemoveFileForAccount = accountName => {
		this.setState(prev => {
			const fileByAccount = { ...prev.fileByAccount };
			delete fileByAccount[accountName];
			return { fileByAccount };
		});
	};

	onTieAccount = ({ account, accountId }) => {
		const { onTieAccount } = this.props;
		if (!onTieAccount || !account.bankAccountId || !accountId) return;
		onTieAccount({ bankAccountId: account.bankAccountId, accountId });
	};

	onPickChartAccountForNewRow = (index, accountId) => {
		this.setState(prev => {
			const accounts = prev.accounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, postsToAccountId: accountId || null } : account
			);
			return { accounts };
		});
	};

	onAddChartAccountForNewRow = async index => {
		const { onAddChartAccount } = this.props;
		if (!onAddChartAccount) return;
		const account = this.state.accounts[index];
		const outcome = await onAddChartAccount({ accountName: account.name });
		if (outcome && outcome.accountId) this.onPickChartAccountForNewRow(index, outcome.accountId);
	};

	addAccountName = name => {
		const cleanName = (name || "").trim();
		if (!cleanName) return;
		const alreadyListed = this.state.accounts.some(account => account.name === cleanName);
		if (alreadyListed) return;
		this.setState(prev => ({ accounts: [...prev.accounts, accountRowFromAName({ name: cleanName })] }));
	};

	addStatementFiles = fileList => {
		const files = Array.from(fileList || []);
		if (files.length === 0) return;

		this.setState(prev => {
			const accounts = [...prev.accounts];
			const fileByAccount = { ...prev.fileByAccount };
			for (const file of files) {
				const accountName = UtilityService.getFileNameWithoutExtension(file.name);
				const alreadyListed = accounts.some(account => account.name === accountName);
				if (!alreadyListed) accounts.push(accountRowFromAName({ name: accountName }));
				const isPdf = UtilityService.isPdfFile(file.name);
				fileByAccount[accountName] = isPdf ? { files: [file], kind: "pdf" } : { files: [file], kind: "spreadsheet" };
			}
			return { accounts, fileByAccount };
		});
	};

	onAddFromPicker = accountName => {
		this.addAccountName(accountName);
	};

	onCustomNameChange = event => {
		this.update({ customName: event.target.value });
	};

	onAddCustom = () => {
		this.addAccountName(this.state.customName);
		this.update({ customName: "" });
	};

	componentDidMount = () => {
		this.notifyDraft();
	};

	componentDidUpdate = (prevProps, prevState) => {
		if (prevProps.accountsOnTheFile !== this.props.accountsOnTheFile) this.takeInWhatTheFileNowSays();

		const draftChanged = prevState.accounts !== this.state.accounts || prevState.currencyByAccount !== this.state.currencyByAccount || prevState.fileByAccount !== this.state.fileByAccount;
		if (draftChanged) this.notifyDraft();
	};

	takeInWhatTheFileNowSays = () => {
		const accountsOnTheFile = this.props.accountsOnTheFile || [];
		const fileRowByName = {};
		for (const account of accountsOnTheFile) fileRowByName[account.name] = accountRowFromTheFile({ account });

		const saysTheSame = (row, fromTheFile) =>
			row.bankAccountId === fromTheFile.bankAccountId && row.postsTo === fromTheFile.postsTo && row.postsToAccountId === fromTheFile.postsToAccountId && row.storedCurrency === fromTheFile.storedCurrency;

		this.setState(prev => {
			const namesListed = new Set(prev.accounts.map(account => account.name));
			let somethingChanged = false;
			const refreshed = prev.accounts.map(account => {
				const fromTheFile = fileRowByName[account.name];
				if (!fromTheFile || saysTheSame(account, fromTheFile)) return account;
				somethingChanged = true;
				return { ...fromTheFile, included: account.included };
			});
			const newlyOnTheFile = accountsOnTheFile.filter(account => !namesListed.has(account.name)).map(account => fileRowByName[account.name]);
			if (!somethingChanged && newlyOnTheFile.length === 0) return null;
			return { accounts: [...refreshed, ...newlyOnTheFile] };
		});
	};

	buildContinuePayload = () => {
		const includedAccounts = this.state.accounts.filter(account => account.included && account.name.trim());
		const bankAccountNames = includedAccounts.map(account => account.name.trim());
		const fileByAccount = {};
		const currencyByAccount = {};
		const postsToAccountIdByBankAccount = {};
		const untiedAccountNames = [];
		for (const account of includedAccounts) {
			const name = account.name.trim();
			if (this.state.fileByAccount[name]) fileByAccount[name] = this.state.fileByAccount[name];
			currencyByAccount[name] = this.currencyCodeFor(account);
			if (account.postsToAccountId && !account.isOnTheFile) postsToAccountIdByBankAccount[name] = account.postsToAccountId;
			if (!account.postsToAccountId) untiedAccountNames.push(name);
		}
		const hasUnresolvedIdentityDecisions = includedAccounts.some(account => this.rowNeedsAnIdentityDecision(account));
		return { bankAccountNames, fileByAccount, currencyByAccount, postsToAccountIdByBankAccount, untiedAccountNames, includedCount: bankAccountNames.length, hasUnresolvedIdentityDecisions };
	};

	notifyDraft = () => {
		if (this.props.onDraftChange) this.props.onDraftChange(this.buildContinuePayload());
	};

	describeUploadLabel = ({ attachedFile }) => {
		const hasFile = !!(attachedFile && attachedFile.files && attachedFile.files.length > 0);
		if (!hasFile) return "Upload statement(s)";
		if (attachedFile.files.length === 1) return attachedFile.files[0].name;
		const singular = attachedFile.kind === "pdf" ? "PDF statement" : "statement";
		return UtilityService.describeCount({ count: attachedFile.files.length, singular });
	};

	renderStatementsOnTheShelf = account => {
		const onTheShelf = (this.props.statementsByAccount || {})[account.name];
		if (!onTheShelf) return null;

		const coverage = BookkeepingService.describeStatementCoverage({ count: onTheShelf.count, periodStart: onTheShelf.periodStart, periodEnd: onTheShelf.periodEnd });

		return (
			<span className="tk-coverage" title="Statements already on this client's record for this account, in this period">
				<Icon.CheckCircle size={12} />
				<span>{coverage} already on file</span>
			</span>
		);
	};

	renderAccountName = (account, index) => {
		if (!account.isOnTheFile) {
			return (
				<div className="bank-review__name-block">
					<input
						type="text"
						className="bank-review__name-input"
						value={account.name}
						onChange={event => this.onRenameAccount(index, event.target.value)}
					/>
					{this.renderChartTie(account, index)}
					{this.renderStatementsOnTheShelf(account)}
				</div>
			);
		}

		return (
			<div className="bank-review__name-block">
				<span className="bank-review__name" title="This account is already on the file, so its name is fixed here">{account.name}</span>
				{this.renderChartTie(account, index)}
				{this.renderStatementsOnTheShelf(account)}
			</div>
		);
	};

	renderChartTie = (account, index) => {
		if (account.isOnTheFile && account.postsTo) {
			return <span className="bank-review__tie">Posts to {account.postsTo}</span>;
		}

		const isTied = !!account.postsToAccountId;
		const canBeTied = account.isOnTheFile ? !!(this.props.onTieAccount && account.bankAccountId) : true;
		const onPick = account.isOnTheFile
			? accountId => this.onTieAccount({ account, accountId })
			: accountId => this.onPickChartAccountForNewRow(index, accountId);

		return (
			<span className={clsx("bank-review__tie", !isTied && "bank-review__tie--untied")}>
				{!isTied && <Icon.AlertTriangle size={12} />}
				<span>{isTied ? "Posts to" : "Not tied to the chart"}</span>
				{canBeTied && (
					<ToolsSelect
						inline
						className="tools-select--account-picker"
						value={account.postsToAccountId || ""}
						options={this.props.chartAccountOptions || []}
						placeholder={account.isOnTheFile ? "Tie it to a chart account…" : "Choose the chart account…"}
						onChange={onPick}
						searchable
						searchPlaceholder="Search the chart…"
					/>
				)}
				{!account.isOnTheFile && this.props.onAddChartAccount && (
					<button type="button" className="tk-text-action tk-text-action--xs" onClick={() => this.onAddChartAccountForNewRow(index)}>
						New chart account…
					</button>
				)}
			</span>
		);
	};

	renderCurrency = account => (
		<span className="bank-review__currency" title="Currency of this account — non-CAD is converted to CAD at the Bank of Canada rate">
			<ToolsSelect
				value={this.currencyCodeFor(account)}
				options={CURRENCY_OPTIONS}
				onChange={currencyCode => this.onPickCurrency({ account, currencyCode })}
				searchable
				searchPlaceholder="Search currencies…"
			/>
		</span>
	);

	renderCurrencyChangeConfirmation = () => {
		const { currencyChangeToConfirm, changingCurrency } = this.state;
		if (!currencyChangeToConfirm) return null;

		const { account, currencyCode } = currencyChangeToConfirm;
		const onTheShelf = (this.props.statementsByAccount || {})[account.name];
		const statements = UtilityService.describeCount({ count: onTheShelf ? onTheShelf.count : 0, singular: "statement" });
		const transactions = UtilityService.describeCount({ count: (this.props.transactionCountByAccount || {})[account.name] || 0, singular: "transaction" });

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" disabled={changingCurrency} onClick={this.onCancelCurrencyChange}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" disabled={changingCurrency} onClick={this.onConfirmCurrencyChange}>
						{changingCurrency ? "Changing…" : `Change to ${currencyCode}`}
					</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={this.onCancelCurrencyChange} title={`Change ${account.name} to ${currencyCode}?`} width={520} footer={footer}>
				<p className="bank-review__confirm-line">
					This account is recorded in <strong>{account.storedCurrency}</strong>. Its {statements} and{" "}
					<strong>{transactions}</strong> will be treated as {currencyCode} amounts and converted to CAD at the Bank of Canada rate
					for each date, starting from the amounts the bank printed. Statements you add to it are read the same way.
				</p>
				<p className="bank-review__confirm-line bank-review__confirm-line--muted">
					Switching back to {account.storedCurrency} converts them again from those same printed amounts, so nothing is lost.
				</p>
			</ToolsModal>
		);
	};

	rowNeedsAnIdentityDecision = account => !account.isOnTheFile && !account.identityConfirmedAsNew && (this.props.accountsOnTheFile || []).length > 0;

	renderAccountIdentityPrompt = (account, index) => {
		const existingAccountOptions = (this.props.accountsOnTheFile || []).map(existing => ({ value: existing.name, label: existing.name }));

		return (
			<div className="bank-review__identity-prompt">
				<span className="bank-review__identity-prompt-text">Is "{account.name}" one of these existing accounts?</span>
				<ToolsSelect
					value=""
					options={existingAccountOptions}
					placeholder="Pick an existing account…"
					onChange={existingAccountName => this.onTieRowToExistingAccount({ index, existingAccountName })}
					searchable
				/>
				<button type="button" className="tk-text-action" onClick={() => this.onConfirmRowIsNewAccount(index)}>
					No, this is a new account
				</button>
			</div>
		);
	};

	renderAccountRow = (account, index) => {
		const attachedFile = this.state.fileByAccount[account.name];
		const hasFile = !!(attachedFile && attachedFile.files && attachedFile.files.length > 0);
		const showFilePrompts = hasFile && attachedFile.kind === "spreadsheet" && !!this.props.renderFilePrompts;
		const uploadLabel = this.describeUploadLabel({ attachedFile });
		const needsIdentityDecision = this.rowNeedsAnIdentityDecision(account);

		return (
			<div key={account.isOnTheFile ? `file-${account.bankAccountId}` : `new-${index}`} className={clsx("bank-review__row-wrap", !account.included && "bank-review__row-wrap--excluded")}>
				<div className="bank-review__row">
					<button
						type="button"
						className={clsx("bank-review__include", account.included && "bank-review__include--on")}
						title={account.included ? "Uploading a statement for this account" : "Skipped — no statement"}
						onClick={() => this.onToggleIncluded(index)}
					>
						{account.included ? <Icon.Check size={13} /> : null}
					</button>
					{this.renderAccountName(account, index)}
					<label className="bank-review__upload">
						<span className={clsx("bank-review__upload-btn", hasFile && "bank-review__upload-btn--selected")}>
							<Icon.Upload size={13} />
							<span className="bank-review__upload-text">{uploadLabel}</span>
						</span>
						<input
							type="file"
							accept=".csv,.xlsx,.pdf"
							multiple
							className="bank-review__upload-input"
							onChange={event => this.onSelectFileForAccount(account.name, event)}
						/>
					</label>
					{hasFile && (
						<button type="button" className="bank-review__file-remove" title="Remove this statement" onClick={() => this.onRemoveFileForAccount(account.name)}>
							<Icon.X size={13} />
						</button>
					)}
					{this.renderCurrency(account)}
					{!account.isOnTheFile && (
						<button type="button" className="bank-review__remove" title="Remove this account" onClick={() => this.onRemoveAccount(index)}>
							<Icon.X size={14} />
						</button>
					)}
				</div>
				{needsIdentityDecision && this.renderAccountIdentityPrompt(account, index)}
				{showFilePrompts && (
					<div className="bank-review__sign-prompt">
						{attachedFile.files.map((file, fileIndex) => this.props.renderFilePrompts(file, fileIndex))}
					</div>
				)}
			</div>
		);
	};

	renderSubtitle = () => {
		if (this.props.isAddingToExistingBooks) {
			return (
				<p className="bank-review__subtitle">
					These are the bank and card accounts already on this file. Attach a statement to each one you are adding to,
					or add an account that is missing below.
				</p>
			);
		}

		return (
			<p className="bank-review__subtitle">
				These are the accounts we think are real bank or credit card accounts. Uncheck any that aren't,
				rename them, set each one's currency, and upload its statement. Add any we missed below.
			</p>
		);
	};

	render() {
		const { accounts, customName } = this.state;
		const pickerSource = this.props.pickerSource || "general ledger";

		return (
			<div className="bank-review">
				{this.renderCurrencyChangeConfirmation()}
				<div className="bank-review__header">
					<h3 className="bank-review__title">Confirm the Bank Accounts</h3>
					{this.renderSubtitle()}
				</div>

				<div className="bank-review__list">
					{accounts.length === 0 ? (
						<div className="bank-review__empty">No accounts yet — add the bank accounts below, or drop in statement files.</div>
					) : (
						accounts.map(this.renderAccountRow)
					)}
				</div>

				<FileDropZone
					className="bank-review__dropzone"
					accept=".csv,.xlsx,.pdf"
					multiple
					onFiles={this.addStatementFiles}
					title="Missed an account? Drop its statement here to add it"
					subtitle="each dropped file becomes a new account"
				/>

				<div className="bank-review__add">
					<div className="bank-review__add-title">Add a bank account</div>
					<ToolsSelect
						value=""
						options={this.getRemainingAccountOptions()}
						placeholder={`Pick from your ${pickerSource}…`}
						onChange={this.onAddFromPicker}
						searchable
						searchPlaceholder={`Search your ${pickerSource}…`}
					/>
					<div className="bank-review__add-custom">
						<input
							type="text"
							className="tk-input bank-review__custom-input"
							placeholder="…or type an account name"
							value={customName}
							onChange={this.onCustomNameChange}
						/>
						<button type="button" className="tk-button tk-button--secondary" onClick={this.onAddCustom} disabled={!customName.trim()}>
							Add
						</button>
					</div>
				</div>
			</div>
		);
	}
}

export default BankAccountReview;
